import type { CardEdit, Layer } from '@str/shared';
import type { PictureTool } from './PictureStage';
const PICTURE_COLORS = [
  '#ffffff',
  '#111111',
  '#e85d4a',
  '#edaa35',
  '#f4dc73',
  '#52835b',
  '#477da8',
  '#a671a9',
];
type Props = {
  tool: PictureTool;
  edit: CardEdit;
  onChange: (edit: CardEdit) => void;
  selected: string | null;
  onSelect: (id: string | null) => void;
  editing: string | null;
  onEditing: (id: string | null) => void;
  headline: string;
  color: string;
  onColor: (color: string) => void;
  strokeWidth: number;
  onWidth: (width: number) => void;
  disabled: boolean;
  reel?: boolean;
};
export function PictureTools({
  tool,
  edit,
  onChange,
  selected,
  onSelect,
  editing,
  onEditing,
  headline,
  color,
  onColor,
  strokeWidth,
  onWidth,
  disabled,
  reel = false,
}: Props) {
  const layer = edit.layers.find((l) => l.id === selected);
  const patch = (change: Partial<Layer>) =>
    onChange({
      ...edit,
      layers: edit.layers.map((l) => (l.id === selected ? ({ ...l, ...change } as Layer) : l)),
    });
  const addText = (text = '') => {
    if (edit.layers.length >= 50) return;
    const id = crypto.randomUUID();
    onChange({
      ...edit,
      layers: [
        ...edit.layers,
        {
          id,
          kind: 'text',
          text: text.slice(0, 300),
          x: 0.1,
          y: 0.4,
          w: 0.8,
          size: 0.08,
          font: text ? 'serif' : 'sans',
          color: text ? '#ffffff' : color,
          align: 'center',
          pill: null,
          shade: false,
          rotate: 0,
        },
      ],
    });
    onSelect(id);
    onEditing(id);
  };
  const addEmoji = (char: string) => {
    if (!char.trim() || edit.layers.length >= 50) return;
    const id = crypto.randomUUID();
    onChange({
      ...edit,
      layers: [
        ...edit.layers,
        { id, kind: 'emoji', char: char.slice(0, 16), x: 0.4, y: 0.4, size: 0.15, rotate: 0 },
      ],
    });
    onSelect(id);
    onEditing(null);
  };
  return (
    <fieldset className="picture-tools" disabled={disabled}>
      {tool === 'Text' && (
        <>
          <strong>{reel ? 'Text on your Reel' : 'Text on your picture'}</strong>
          <span className="composer-hint">
            {reel
              ? 'Add a text box, then choose when it appears.'
              : 'Tap the picture or add a text box. Your caption stays separate.'}
          </span>
          <div className="picture-row">
            <button
              type="button"
              className="chip hv"
              disabled={edit.layers.length >= 50}
              onClick={() => addText()}
            >
              Add text
            </button>
            <button
              type="button"
              className="chip hv"
              disabled={!headline || edit.layers.length >= 50}
              onClick={() => addText(headline)}
            >
              Use headline
            </button>
            {layer?.kind === 'text' && (
              <button
                type="button"
                className="chip hv"
                onClick={() => onEditing(editing ? null : layer.id)}
              >
                {editing ? 'Finish typing' : 'Edit text'}
              </button>
            )}
          </div>
          {layer?.kind === 'text' && (
            <div className="picture-row">
              <label>
                Font{' '}
                <select
                  aria-label="Font"
                  value={layer.font}
                  onChange={(e) => patch({ font: e.target.value as 'sans' | 'serif' })}
                >
                  <option value="sans">Sans</option>
                  <option value="serif">Serif</option>
                </select>
              </label>
              <label>
                Align{' '}
                <select
                  aria-label="Align"
                  value={layer.align}
                  onChange={(e) => patch({ align: e.target.value as 'left' | 'center' | 'right' })}
                >
                  <option value="left">Left</option>
                  <option value="center">Centre</option>
                  <option value="right">Right</option>
                </select>
              </label>
              <button
                type="button"
                className="chip hv"
                aria-pressed={Boolean(layer.pill)}
                onClick={() => patch({ pill: layer.pill ? null : '#111111' })}
              >
                Background pill
              </button>
              <button
                type="button"
                className="chip hv"
                aria-pressed={Boolean(layer.shade)}
                onClick={() => patch({ shade: !layer.shade })}
              >
                Shade behind
              </button>
            </div>
          )}
        </>
      )}
      {tool === 'Adjust' && (
        <div className="picture-adjust">
          {(['brightness', 'contrast', 'saturation', 'warmth'] as const).map((key) => (
            <label key={key}>
              {key.charAt(0).toUpperCase() + key.slice(1)}
              <input
                aria-label={key.charAt(0).toUpperCase() + key.slice(1)}
                type="range"
                min={-1}
                max={1}
                step={0.01}
                value={edit.adjust[key]}
                onChange={(e) =>
                  onChange({ ...edit, adjust: { ...edit.adjust, [key]: Number(e.target.value) } })
                }
              />
              <output>{Math.round(edit.adjust[key] * 100)}</output>
            </label>
          ))}
        </div>
      )}
      {tool === 'Sticker' && (
        <>
          <div className="picture-row">
            {['🌞', '🌴', '🏡', '❤️', '✨', '🔥', '☕', '🌊'].map((char) => (
              <button
                type="button"
                className="chip hv"
                key={char}
                aria-label={`Add sticker ${char}`}
                disabled={edit.layers.length >= 50}
                onClick={() => addEmoji(char)}
              >
                {char}
              </button>
            ))}
          </div>
          <form
            className="picture-row"
            onSubmit={(e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const char = new FormData(form).get('emoji') as string;
              addEmoji(char);
              form.reset();
            }}
          >
            <input
              name="emoji"
              aria-label="Other sticker"
              placeholder="Type another emoji"
              maxLength={16}
            />
            <button type="submit" className="chip hv" disabled={edit.layers.length >= 50}>
              Add sticker
            </button>
          </form>
        </>
      )}
      {tool === 'Draw' && (
        <>
          <span className="composer-hint">Draw on the picture with your finger or pointer.</span>
          <div className="picture-row">
            {(
              [
                ['Fine', 0.004],
                ['Medium', 0.012],
                ['Thick', 0.025],
              ] as const
            ).map(([name, width]) => (
              <button
                type="button"
                className="chip hv"
                key={name}
                aria-pressed={strokeWidth === width}
                onClick={() => onWidth(width)}
              >
                {name}
              </button>
            ))}
          </div>
        </>
      )}
      {(tool === 'Text' || tool === 'Draw') && (
        <div className="picture-row" aria-label="Colours">
          {PICTURE_COLORS.map((value, i) => (
            <button
              type="button"
              className="picture-color"
              key={value}
              style={{ background: value }}
              aria-label={
                ['White', 'Black', 'Coral', 'Orange', 'Yellow', 'Green', 'Blue', 'Purple'][i]
              }
              aria-pressed={
                (tool === 'Text' && layer?.kind === 'text' ? layer.color : color) === value
              }
              onClick={() => {
                onColor(value);
                if (tool === 'Text' && layer?.kind === 'text') patch({ color: value });
              }}
            />
          ))}
        </div>
      )}
      {layer && (
        <button
          type="button"
          className="chip hv"
          onClick={() => {
            onChange({ ...edit, layers: edit.layers.filter((l) => l.id !== selected) });
            onSelect(null);
            onEditing(null);
          }}
        >
          Delete item
        </button>
      )}
      {edit.layers.length > 0 && (
        <details className="picture-position">
          <summary>Position and size</summary>
          <p className="composer-hint">
            Drag an item and use its corner handles, or select it and use the controls below.
          </p>
          {edit.layers.length > 0 && tool !== 'Crop' && (
            <div className="picture-row">
              <label>
                Selected item{' '}
                <select
                  aria-label="Selected item"
                  value={selected ?? ''}
                  onChange={(e) => {
                    onSelect(e.target.value || null);
                    onEditing(null);
                  }}
                >
                  <option value="">None</option>
                  {edit.layers.map((l, i) => (
                    <option key={l.id} value={l.id}>
                      {i + 1}.{' '}
                      {l.kind === 'text'
                        ? l.text || 'Empty text'
                        : l.kind === 'emoji'
                          ? l.char
                          : 'Drawing'}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          )}
          {layer && layer.kind !== 'path' && tool !== 'Crop' && (
            <div className="picture-row">
              <label>
                Size{' '}
                <input
                  aria-label="Item size"
                  type="range"
                  min={layer.kind === 'text' ? 0.01 : 0.02}
                  max={layer.kind === 'text' ? 0.5 : 0.8}
                  step={0.005}
                  value={layer.size}
                  onChange={(e) => patch({ size: Number(e.target.value) })}
                />
              </label>
              <label>
                Rotate{' '}
                <input
                  aria-label="Item rotation"
                  type="range"
                  min={-180}
                  max={180}
                  value={layer.rotate}
                  onChange={(e) => patch({ rotate: Number(e.target.value) })}
                />
              </label>
              {(['x', 'y'] as const).map((axis) => (
                <label key={axis}>
                  {axis === 'x' ? 'Across' : 'Down'}
                  <input
                    aria-label={axis === 'x' ? 'Item across' : 'Item down'}
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={layer[axis]}
                    onChange={(e) => patch({ [axis]: Number(e.target.value) })}
                  />
                </label>
              ))}
            </div>
          )}
        </details>
      )}
      {edit.layers.length >= 50 && (
        <span role="status">50 items added. Delete an item to add another.</span>
      )}
    </fieldset>
  );
}
